import { useState } from 'react'
import { Dropdown } from '@/React/Shared/Component/Dropdown'
import { DropdownItem } from '@/React/Shared/Component/DropdownItem'
import { Image } from '@/React/Shared/Component/Image'
import { ApiForm, useApiForm } from '@/React/Shared/Component/Forms/ApiForm'
import { RoutePathAppEnum } from '@/Shared/AutoGenerated/RoutePathEnum'
import type User from '@/App/User/Entity/User'
import type CompanyUser from '@/App/CompanyUser/Entity/CompanyUser'

export interface ProfileDropdownProps {
    authUser: User
    authCompanyUser: CompanyUser
    impersonating: boolean
    className?: string
}

export function ProfileDropdown({
    authUser,
    authCompanyUser,
    impersonating,
    className = '',
}: ProfileDropdownProps) {
    const [isOpen, setIsOpen] = useState(false)
    const leaveImpersonateApiForm = useApiForm()
    const logoutApiForm = useApiForm()

    return (
        <Dropdown
            show={isOpen}
            onClose={() => setIsOpen(false)}
            className={className}
        >
            <Dropdown.Trigger>
                <button
                    type="button"
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    aria-label={`User profile menu for ${authUser.firstName}`}
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="flex items-center gap-3 cursor-pointer bg-transparent border-0 p-1 rounded-lg focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
                >
                    <span className="hidden md:block text-gray-700">
                        {authUser.firstName}
                    </span>
                    <Image
                        className="rounded-full w-8 h-8 object-cover"
                        src={authUser.getProfilePictureUrlWithFallback()}
                        alt={authUser.firstName}
                    />
                </button>
            </Dropdown.Trigger>
            <Dropdown.Menu>
                <div>
                    {authCompanyUser.isAdminOrOwner() ? (
                        <DropdownItem href={RoutePathAppEnum.Company}>
                            Company
                        </DropdownItem>
                    ) : null}
                    <DropdownItem href={RoutePathAppEnum.MyProfile}>
                        My Profile
                    </DropdownItem>
                    {authUser.admin ? (
                        <DropdownItem href={RoutePathAppEnum.Admin_Users}>
                            Users
                        </DropdownItem>
                    ) : null}
                    {impersonating ? (
                        <DropdownItem
                            onClick={() => leaveImpersonateApiForm.submit()}
                        >
                            Leave Impersonate
                        </DropdownItem>
                    ) : null}
                    <DropdownItem onClick={() => logoutApiForm.submit()}>
                        Logout
                    </DropdownItem>
                    <ApiForm
                        apiForm={leaveImpersonateApiForm}
                        url={RoutePathAppEnum.Admin_Users_LeaveImpersonateMode}
                        method="POST"
                    />
                    <ApiForm
                        apiForm={logoutApiForm}
                        url={RoutePathAppEnum.Auth_Logout}
                        method="POST"
                    />
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}
